import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { AuthService } from "app/auth/auth.service";
import { LoadingController, ToastController } from "ionic-angular";
import { Assessment } from "models/assessment/assessment";
import { DeathInformation } from "models/assessment/death-information";
import Visit from "models/patient-chart/visit";
import * as moment from "moment-timezone";
import { Observable } from "rxjs/Observable";
import { of } from "rxjs/observable/of";
import { catchError, finalize, map, shareReplay, tap } from "rxjs/operators";
import { HopeVisitSignatureDto } from "../../models/hope/hope-type";
import { PatientHopeDataResponse } from "../../models/patient-chart/patientHopeDataResponse";
import { ConfigurationService } from "../configuration/configuration.service";
import { StateManagementService } from "../state-management/state-management.service";

const URI_ASSESSMENT: any = {
  assessment: "/assessment/",
  cancelVisit: (idAssessment: number) => `${idAssessment}/cancel`,
  acquireLock: (id: number) => `${id}/lock`,
  releaseLock: (id: number) => `${id}/lock`,
};

@Injectable()
export class AssessmentDataService {
  spsUrl: string;
  dataUrl: string;
  headerData: any = null;
  noopObs = new Observable((observer) => {
    observer.complete();
  });

  spinner = null;
  spinnerCounter = 0;
  private hopeDataCache = new Map<
    number,
    Observable<PatientHopeDataResponse>
  >();

  constructor(
    public httpClient: HttpClient,
    public toastCtrl: ToastController,
    public stateSvc: StateManagementService,
    public loadingCtrl: LoadingController,
    public translatePipe: TranslateService,
    private config: ConfigurationService,
    private authSvc: AuthService
  ) {
    if (this.config.getConfig().spsUrl) {
      this.spsUrl = this.config.getConfig().spsUrl;
    } else {
      this.spsUrl = this.config.getConfig().apiUrl;
    }
    this.dataUrl = config.getConfig().apiUrl;
    this.headerData = {
      ...this.authSvc.getUser(),
      ...this.stateSvc.getUserData(),
    };
  }

  loadCards(assessmentId: string, sectionId: string): Observable<any> {
    if (!assessmentId || assessmentId === "undefined") {
      return this.noopObs;
    }

    let url = `${this.dataUrl}/visits/${assessmentId}/sections/${sectionId}`;
    return new Observable((observer) => {
      this.increaseSpinnerCount();
      this.httpClient.get(url).subscribe(
        (data) => {
          this.decreaseSpinnerCount();
          observer.next(data);
          observer.complete();
        },
        (err: HttpErrorResponse) => {
          this.presentErrorToast(err, "Failed to load section data.");
          this.decreaseSpinnerCount();
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  storeCards(
    assessmentId: string,
    section: string,
    data: object,
    isVisitDeleted: boolean
  ): Observable<any> {
    if (!this.isAssessmentCompleted() && !isVisitDeleted) {
      let url: string = `${this.dataUrl}/visits/${assessmentId}/sections/${section}`;
      return new Observable((observer) => {
        this.increaseSpinnerCount("Saving...");
        this.httpClient.put(url, data).subscribe(
          (data) => {
            this.presentSuccessToast("Successfully saved card data.");
            observer.next(data);
            observer.complete();
            this.decreaseSpinnerCount();
            this.stateSvc.notifyDataStateChange();
          },
          (err) => {
            this.presentErrorToast(err, "Failed to store card data.");
            this.decreaseSpinnerCount();
            observer.error(err);
            observer.complete();
          }
        );
      });
    } else {
      return new Observable((observer) => {
        observer.next("");
        observer.complete();
        this.stateSvc.notifyDataStateChange();
      });
    }
  }

  storeCompletedAssessmentCards(
    assessmentId: string,
    section: string,
    data: object,
    isVisitDeleted: boolean
  ): Observable<any> {
    let url: string = `${this.dataUrl}/visits/${assessmentId}/sections/${section}`;
    return new Observable((observer) => {
      this.increaseSpinnerCount("Saving...");
      this.httpClient.put(url, data).subscribe(
        (data) => {
          this.presentSuccessToast("Successfully saved card data.");
          observer.next(data);
          observer.complete();
          this.decreaseSpinnerCount();
          this.stateSvc.notifyDataStateChange();
        },
        (err) => {
          this.presentErrorToast(err, "Failed to store card data.");
          this.decreaseSpinnerCount();
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  storeRecord(
    assessmentId: string,
    section: string,
    card: string,
    data: object,
    recordType?: string,
    toaster = true
  ): Observable<any> {
    if (this.isAssessmentCompleted() == false) {
      if (!recordType) {
        recordType = "record";
      }
      recordType = this.translatePipe.instant(recordType);

      let url: string = `${this.dataUrl}/visits/${assessmentId}/sections/${section}/cards/${card}`;
      return new Observable((observer) => {
        if (toaster) {
          this.increaseSpinnerCount("Saving...");
        }
        this.httpClient.post(url, data).subscribe(
          (data) => {
            if (toaster) {
              this.presentSuccessToast(
                "Successfully saved " + recordType + " data."
              );
              this.decreaseSpinnerCount();
            }
            observer.next(data);
            observer.complete();
            this.stateSvc.notifyDataStateChange();
          },
          (err) => {
            if (toaster) {
              this.presentErrorToast(
                err,
                "Failed to store " + recordType + " data."
              );
              this.decreaseSpinnerCount();
            }
            observer.error(err);
            observer.complete();
          }
        );
      });
    }
    return Observable.empty();
  }

  editRecord(
    assessmentId: string,
    section: string,
    card: string,
    id: string,
    data: object,
    toaster = true
  ): Observable<any> {
    if (this.isAssessmentCompleted() == false) {
      let url: string = `${this.dataUrl}/visits/${assessmentId}/sections/${section}/cards/${card}/${id}`;
      return new Observable((observer) => {
        if (toaster) {
          this.increaseSpinnerCount("Updating Record...");
        }
        this.httpClient.put(url, data).subscribe(
          (data) => {
            if (toaster) {
              this.presentSuccessToast("Successfully updated record data.");
              this.decreaseSpinnerCount();
            }
            observer.next(data);
            observer.complete();
            this.stateSvc.notifyDataStateChange();
          },
          (err) => {
            if (toaster) {
              this.presentErrorToast(err, "Failed to update record.");
              this.decreaseSpinnerCount();
            }
            observer.error(err);
            observer.complete();
          }
        );
      });
    }
    return Observable.empty();
  }

  deleteRecord(
    assessmentId: string,
    sectionId: string,
    cardId: string,
    id: string
  ): Observable<any> {
    let url: string = `${this.dataUrl}/visits/${assessmentId}/sections/${sectionId}/cards/${cardId}/${id}`;
    return new Observable((observer) => {
      this.increaseSpinnerCount("Deleting Record...");
      this.httpClient.delete(url).subscribe(
        (data) => {
          observer.next();
          observer.complete();
          this.decreaseSpinnerCount();
          this.stateSvc.notifyDataStateChange();
        },
        (err) => {
          this.presentErrorToast(err, "Failed to delete record.");
          observer.error(err);
          observer.complete();
          this.decreaseSpinnerCount();
        }
      );
    });
  }

  getAddendums(assessmentId: string, addendumType?: string): Observable<any> {
    addendumType = addendumType || "all";
    let url: string = `${this.dataUrl}/visits/${assessmentId}/addendums?type=${addendumType}`;

    return new Observable((observer) => {
      this.increaseSpinnerCount("Loading Records...");
      this.httpClient.get(url).subscribe(
        (data) => {
          this.decreaseSpinnerCount();
          this.stateSvc.notifyDataStateChange();
          observer.next(data);
        },
        (err) => {
          this.decreaseSpinnerCount();
          this.presentErrorToast(err, "Failed to load records.");
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  regenerateReport(assessmentId) {
    let spsUrl = `${
      this.config.getConfig().spsUrl
    }/visits/${assessmentId}/regenreports`;
    return this.httpClient.post(spsUrl, {});
  }

  postAddendum(assessmentId: string, data: any): Observable<any> {
    let url: string = `${this.dataUrl}/visits/${assessmentId}/addendums`;

    return new Observable((observer) => {
      this.increaseSpinnerCount("Loading Addendum...");
      this.httpClient.post(url, data).subscribe(
        (data) => {
          this.presentSuccessToast("Successfully saved addendum.");

          this.decreaseSpinnerCount();
          this.stateSvc.notifyDataStateChange();
          this.regenerateReport(assessmentId).subscribe();
          observer.next(data); // ????
          observer.complete();
        },
        (err) => {
          this.presentErrorToast(err, "Failed to save addendums.");
          this.decreaseSpinnerCount();
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  updateAddendum(
    addendumId: string,
    assessmentId: string,
    data: any
  ): Observable<any> {
    let url: string = `${this.dataUrl}/addendums/${addendumId}`;

    return new Observable((observer) => {
      this.increaseSpinnerCount("Updating Addendum...");
      this.httpClient.put(url, data).subscribe(
        (data) => {
          this.decreaseSpinnerCount();
          this.stateSvc.notifyDataStateChange();
          this.regenerateReport(assessmentId).subscribe();
          observer.next(data);
        },
        (err) => {
          this.presentErrorToast(err, "Failed to update addendum.");
          this.decreaseSpinnerCount();
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  findTemplateSection(sectionLookupCode: String): Observable<any> {
    let url: string = `${this.dataUrl}/visits/template/section/${sectionLookupCode}`;

    return new Observable((observer) => {
      this.increaseSpinnerCount();
      this.httpClient.get(url).subscribe(
        (data) => {
          this.decreaseSpinnerCount();
          this.stateSvc.notifyDataStateChange();
          observer.next(data);
        },
        (err) => {
          this.presentErrorToast(
            err,
            "Failed to retrieve template section data."
          );
          this.decreaseSpinnerCount();
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  deleteAddendum(
    addendumId: string,
    assessmentId: string,
    data: any
  ): Observable<any> {
    let url: string = `${this.dataUrl}/addendums/${addendumId}`;

    return new Observable((observer) => {
      this.increaseSpinnerCount("Deleting Record...");
      let options: any = { body: data };
      this.httpClient.delete(url, options).subscribe(
        (data) => {
          this.decreaseSpinnerCount();
          this.stateSvc.notifyDataStateChange();
          this.regenerateReport(assessmentId).subscribe();
          observer.next();
        },
        (err) => {
          this.presentErrorToast(err, "Failed to delete record.");
          this.decreaseSpinnerCount();
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  validateCompletionStatus(
    assessmentId: string,
    hasDuplicateDiagnosis: boolean,
    HOPEToggle: boolean
  ): Observable<any> {
    let url: string =
      this.dataUrl + "/visits/" + assessmentId + "/completion-status";
    return new Observable((observer) => {
      this.increaseSpinnerCount("Verifying Assessment Status...");
      const reportType = HOPEToggle ? "HOPE" : "HIS";
      this.httpClient.get(url).subscribe(
        (data) => {
          var msgs = "";
          var palliative = false;
          var messages = data;
          let assessmentTypeLookupCode =
            sessionStorage.getItem("asmtTypeLookupCode");
          if (messages != null) {
            if (hasDuplicateDiagnosis)
              this.addDuplicateDiagnosisError(messages);
            if (messages["levelOfCare"] == "Palliative") {
              palliative = true;
            }

            if (messages["status"] == false) {
              for (let i = 0; i < messages["statusList"].length; i++) {
                //Logic for social worker palliative visit
                if (
                  messages["levelOfCare"] == "Palliative" &&
                  assessmentTypeLookupCode == "CL003.25"
                ) {
                  if (messages["statusList"].length > 0) {
                    let status = messages["statusList"][i].status;
                    if (status == true) {
                      observer.next(data["status"]);
                      this.decreaseSpinnerCount();
                      break;
                    } else {
                      msgs += messages["statusList"][i].msg + " \n";
                    }
                  }
                }
                //logic for normal palliative visit
                else if (
                  messages["levelOfCare"] == "Palliative" &&
                  assessmentTypeLookupCode != "CL003.25"
                ) {
                  if (messages["statusList"].length > 0) {
                    let status = messages["statusList"][i].status;
                    let type = messages["statusList"][i].type;
                    if (status == true) {
                      observer.next(data["status"]);
                      this.decreaseSpinnerCount();
                      break;
                    } else if (type != "pocSnapshot") {
                      msgs += messages["statusList"][i].msg + " \n";
                    }
                  }
                } else if (messages["levelOfCare"] != "Palliative") {
                  if (messages["statusList"].length > 0) {
                    let status = messages["statusList"][i].status;
                    if (status == true) {
                      observer.next(data["status"]);
                      this.decreaseSpinnerCount();
                      break;
                    } else {
                      msgs += messages["statusList"][i].msg + " \n";
                    }
                  }
                }
              }
              if (
                assessmentTypeLookupCode == "CL003.12" &&
                this.stateSvc &&
                this.stateSvc.hisV2.getValue() &&
                (!sessionStorage.getItem("reviewedHQRPPreview") ||
                  sessionStorage.getItem("reviewedHQRPPreview") == "false")
              ) {
                msgs += `Please review ${reportType} Preview Report \n`;
                this.decreaseSpinnerCount();
              }
            } else if (
              assessmentTypeLookupCode == "CL003.12" &&
              this.stateSvc &&
              this.stateSvc.hisV2.getValue() &&
              (!sessionStorage.getItem("reviewedHQRPPreview") ||
                sessionStorage.getItem("reviewedHQRPPreview") == "false")
            ) {
              msgs += `Please review ${reportType} Preview Report \n`;
              this.decreaseSpinnerCount();
            } else {
              observer.next(data);
              this.decreaseSpinnerCount();
              observer.complete();
            }
          }

          if (msgs != "" && palliative == false) {
            this.customErrorToast(msgs);
            observer.error(msgs);
            observer.complete();
            this.decreaseSpinnerCount();
          } else if (palliative == true) {
            if (msgs != "") {
              this.customErrorToast(msgs);
              observer.error(msgs);
              observer.complete();
              this.decreaseSpinnerCount();
            } else {
              observer.next(data);
              this.decreaseSpinnerCount();
              observer.complete();
            }
          }
        },
        (err) => {
          this.presentErrorToast(
            err,
            "Failed to validate assessment completion status."
          );
          observer.error(err);
          observer.complete();
          this.decreaseSpinnerCount();
        }
      );
    });
  }

  completeAssessment(assessmentId: string, data: object): Observable<any> {
    let url: string = this.spsUrl + "/visits/" + assessmentId + "/complete";
    return new Observable((observer) => {
      this.increaseSpinnerCount("Completing Assessment...");
      this.httpClient.post(url, data).subscribe(
        (data) => {
          var msgs = "";
          var messages = data;
          if (messages != null && messages != undefined) {
            for (var i = 0; i < messages["statusList"].length; i++) {
              if (messages["statusList"].length > 0) {
                let status = messages["statusList"][i].status;
                if (status == true) {
                  this.presentSuccessToast("Visit completed successfully");
                  observer.next();
                  this.decreaseSpinnerCount();
                  break;
                } else {
                  msgs += messages["statusList"][i].msg + " \n";
                }
              }
            }
          }

          if (msgs != "") {
            this.customErrorToast(msgs);
            observer.error(msgs);
            observer.complete();
            this.decreaseSpinnerCount();
          }
        },
        (err) => {
          if (err.error && JSON.parse(err.error)) {
            this.presentErrorToast(err, JSON.parse(err.error).errorMessage);
          }
          observer.error(err);
          observer.complete();
          this.decreaseSpinnerCount();
        }
      );
    });
  }

  signElectronicSignature(assessmentId: string, obj: object): Observable<any> {
    let url: string = this.dataUrl + "/visits/" + assessmentId + "/signatures";
    return new Observable((observer) => {
      this.increaseSpinnerCount("Completing Electronic Signature...");
      this.httpClient.post(url, obj).subscribe(
        (data) => {
          observer.next(data);
          this.decreaseSpinnerCount();
        },
        (err) => {
          this.presentErrorToast(err, JSON.parse(err.error).errorMessage);
          observer.error(err);
          observer.complete();
          this.decreaseSpinnerCount();
        }
      );
    });
  }

  cancelVisitWithoutAddNewMedicalChecks(assessmentId: string) {
    const url: string = `${this.dataUrl}/visits/${assessmentId}/cancel`;
    const emptyBody = {};
    return new Observable((observer) => {
      this.increaseSpinnerCount("Deleting Record...");
      this.httpClient.post(url, emptyBody).subscribe(
        (data) => {
          observer.next();
          this.decreaseSpinnerCount();
          this.stateSvc.notifyDataStateChange();
        },
        (err) => {
          this.presentErrorToast(err, "Failed to delete record.");
          observer.error(err);
          observer.complete();
          this.decreaseSpinnerCount();
        }
      );
    });
  }

  cancelVisitWithMedicalChecksAndReason(
    assessmentId: string,
    code: number,
    notes: string
  ) {
    const url: string = `${this.dataUrl}/visits/${assessmentId}/cancel`;
    const data = {
      code: code,
      notes: notes,
    };
    return new Observable((observer) => {
      this.increaseSpinnerCount("Deleting Record...");
      this.httpClient.post(url, data).subscribe(
        (data) => {
          observer.next();
          this.decreaseSpinnerCount();
          this.stateSvc.notifyDataStateChange();
        },
        (err) => {
          this.presentErrorToast(err, "Failed to delete record.");
          observer.error(err);
          observer.complete();
          this.decreaseSpinnerCount();
        }
      );
    });
  }

  getCancelVisitReasons(): Observable<any> {
    let url: string = `${this.dataUrl}/visits/cancelReasons`;
    return new Observable((observer) => {
      this.increaseSpinnerCount();
      this.httpClient.get(url).subscribe(
        (data) => {
          this.decreaseSpinnerCount();
          observer.next(data);
          observer.complete();
        },
        (err: HttpErrorResponse) => {
          this.presentErrorToast(err, "Failed to load cancel visit reasons.");
          this.decreaseSpinnerCount();
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  hqrpReportPreview(assessmentId: string, reportType: string) {
    return new Observable((observer) => {
      this.increaseSpinnerCount(
        `Loading ${reportType.toUpperCase()} report...`
      );
      this.httpClient
        .get(`${this.dataUrl}/hqrp/visits/${assessmentId}/${reportType}`)
        .subscribe(
          (data) => {
            observer.next(data);
            this.decreaseSpinnerCount();
            observer.complete();
          },
          (err) => {
            this.customErrorToast(
              `Failed to load ${reportType.toUpperCase()} report.`
            );
            this.decreaseSpinnerCount();
            observer.complete();
          }
        );
    });
  }

  getOpenAssessments(patientChartId: string): Observable<any> {
    let url: string = `${this.dataUrl}/visits/patientChartId/${patientChartId}/open`;

    return new Observable((observer) => {
      this.httpClient.get(url).subscribe(
        (data) => {
          this.decreaseSpinnerCount();
          observer.next(data);
        },
        (err) => {
          this.decreaseSpinnerCount();
          this.presentErrorToast(err, "Failed to load list of assessments");
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  // CO-3455: passive open-visit indicator. Same endpoint + sort VisitsPage's
  // own Visits grid uses (see getVisitsData in visits.ts) so the response
  // shares its field shape (asmt_status_lookup_code, role_lookup_value,
  // performed_by, started_dte, timezone, _id).
  getVisitsForOpenVisitIndicator(patChartId: string): Observable<any> {
    let url: string = `${this.dataUrl}/patient-charts/${patChartId}/visits?$sort=asmt_completed_dte desc, _id desc`;
    return this.httpClient.get(url);
  }

  getWoundCardHistory(
    patientChartId: string,
    assessmentId: string,
    cardId: string
  ): Observable<any> {
    let url: string = `${this.dataUrl}/patient-charts/${patientChartId}/visits/${assessmentId}/history/CL010.284`;

    return new Observable((observer) => {
      this.increaseSpinnerCount("Getting Wound History...");
      this.httpClient.get(url).subscribe(
        (data) => {
          this.decreaseSpinnerCount();
          this.stateSvc.notifyDataStateChange();
          observer.next(data);
        },
        (err) => {
          this.decreaseSpinnerCount();
          this.presentErrorToast(err, "Failed to load Wound History.");
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  checkClaimsForBilling(patientChardId, assessmentId: string) {
    return new Observable((observer) => {
      this.httpClient
        .get(
          `${this.dataUrl}/patientCharts/${patientChardId}/visits/${assessmentId}/claim/status`
        )
        .subscribe(
          (data) => {
            if (
              data &&
              (data["status"] === "Submitted" || data["status"] === "Pending")
            ) {
              this.customErrorToast(
                "A claim has already gone out. There are no updates allowed after the claim has been sent."
              );
              observer.next(false);
              observer.complete();
            } else {
              observer.next(true);
              observer.complete();
            }
          },
          (err) => {
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  updateSnapshot(assessmentId: string, sectionId: string) {
    return new Observable((observer) => {
      this.increaseSpinnerCount("updating data...");
      this.httpClient
        .get(
          `${this.dataUrl}/visits/${assessmentId}/sections/${sectionId}/updateSnapshot`
        )
        .subscribe(
          (data) => {
            this.decreaseSpinnerCount();
            observer.next(data);
            observer.complete();
          },
          (err) => {
            this.decreaseSpinnerCount();
            this.customErrorToast("Failed to store data.");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  createVisit(data: Visit): Observable<Visit> {
    return new Observable<Visit>((observer) => {
      this.httpClient.post(`${this.dataUrl}/visits`, data).subscribe(
        (data: Visit) => {
          observer.next(data);
          observer.complete();
        },
        (err) => {
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  presentSuccessToast(message: string) {
    let toast = this.toastCtrl.create({
      message: message,
      duration: 3000,
      position: "below",
      cssClass: "success",
    });

    toast.present();
  }

  presentErrorToast(err: HttpErrorResponse, message: string) {
    if (err && (err as any).code === "REFRESH_TOKEN_FAILED") {
      return;
    }
    message += " ";
    if (err.error instanceof Error) {
      message += "Client error.";
    } else {
      message += " ";
      if (err.status == 401) {
        message += "User not authorized.";
      } else if (err.status >= 500) {
        message += "Server error.";
      } else {
        message += "Communication error.";
      }
    }

    let toast = this.toastCtrl.create({
      message: message,
      duration: 4000,
      position: "below",
      cssClass: "error",
    });

    toast.present();
  }

  customErrorToast(message: string) {
    message += " ";

    let toast = this.toastCtrl.create({
      message: message,
      duration: 8000,
      position: "below",
      cssClass: "error",
    });

    toast.present();
  }

  increaseSpinnerCount(message: string = "Loading data...") {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0) {
      if (this.spinner) {
        this.spinner.dismiss();
      }
      this.spinner = null;
    }
  }

  isAssessmentCompleted() {
    if (this.stateSvc.hasNavigationStatus) {
      if (this.authSvc.authenticated()) {
        // const userID =  +this.headerData.profile.userId;
        const userID = this.authSvc.getUser().userId;
        if (
          this.stateSvc.navigationStatusData["statusLookupCode"] == "CL004.3"
        ) {
          return true;
        } else if (
          this.stateSvc.navigationStatusData["statusLookupCode"] != "CL004.3" &&
          this.stateSvc.navigationStatusData["createdBy"] != userID
        ) {
          return true;
        } else {
          return false;
        }
      }
    }
  }

  getHospiceAideTaskHistory(
    historyId: string,
    patientChartId: string,
    currentWeek: string,
    previousWeek: string
  ) {
    return new Observable((observer) => {
      let params = new HttpParams();
      params = params.append("historyId", historyId);
      params = params.append("patientChartId", patientChartId);
      params = params.append("currentWeek", currentWeek);
      params = params.append("previousWeek", previousWeek);

      this.increaseSpinnerCount("Loading history...");
      this.httpClient
        .get(`${this.dataUrl}/hospiceaidehistory`, { params: params })
        .subscribe(
          (data) => {
            this.decreaseSpinnerCount();
            observer.next(data);
            observer.complete();
          },
          (err) => {
            this.decreaseSpinnerCount();
            this.customErrorToast("Failed to retrieve history data.");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  findEvaluationVisits(patientChartId: string) {
    return new Observable((observer) => {
      this.httpClient
        .get(
          `${this.dataUrl}/assessment/evaluationAssessments/${patientChartId}`
        )
        .subscribe(
          (data) => {
            observer.next(data);
            observer.complete();
          },
          (err) => {
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  addDuplicateDiagnosisError(messages: any) {
    messages["status"] = false;
    if (messages["statusList"] != null) {
      messages["statusList"].push({
        status: false,
        type: "assessmentSectionStatus",
        msg: "Diagnosis you selected already exist, please select a different diagnosis.",
      });
    } else {
      messages["statusList"] = [
        {
          status: false,
          type: "assessmentSectionStatus",
          msg: "Diagnosis you selected already exist, please select a different diagnosis.",
        },
      ];
    }
  }

  getSymptomImpactSummary(assessmentId: string): Observable<any> {
    let url: string = `${this.dataUrl}/visits/${assessmentId}/symptom-impact-summary`;
    return new Observable((observer) => {
      this.increaseSpinnerCount();
      this.httpClient.get(url).subscribe(
        (data) => {
          this.decreaseSpinnerCount();
          observer.next(data);
          observer.complete();
        },
        (err: HttpErrorResponse) => {
          this.presentErrorToast(err, "Failed to load symptom impact summary.");
          this.decreaseSpinnerCount();
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  syncSymptomImpactSummary(assessmentId: string): void {
    const url = `${this.dataUrl}/visits/${assessmentId}/symptom-impact-summary-sync`;
    this.httpClient.post(url, {}).subscribe();
  }

  getPatientHopeDatasByPatientChartId(
    patChartId: number,
    reportType: "hope"
  ): Observable<PatientHopeDataResponse | undefined> {
    // Deduplicate concurrent calls for the same patient (calendar page triggers
    // this from checkHQRP + two huv-notification components simultaneously)
    if (this.hopeDataCache.has(patChartId)) {
      return this.hopeDataCache.get(patChartId);
    }

    this.increaseSpinnerCount();

    let timezone = moment.tz.guess();
    let url: string = `${this.dataUrl}/patient-chart/${patChartId}/hqrp/${reportType}?timezone=${timezone}`;
    const shared$ = this.httpClient.get<PatientHopeDataResponse>(url).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status !== 404) {
          this.presentErrorToast(err, "Failed to load patient hope data.");
        }
        return of(undefined);
      }),
      finalize(() => {
        this.decreaseSpinnerCount();
        this.hopeDataCache.delete(patChartId);
      }),
      shareReplay(1)
    );

    this.hopeDataCache.set(patChartId, shared$);
    return shared$;
  }

  updateHopeNotificationOverrides(
    patChartId: number,
    disableHopeNotifications: boolean
  ): Observable<boolean> {
    const url = `${this.dataUrl}/patient-chart/${patChartId}/hqrp/hope/notification-overrides`;
    const params = new HttpParams().set(
      "disableHopeNotifications",
      String(disableHopeNotifications)
    );

    return this.httpClient.put(url, {}, { params }).pipe(
      tap(() => this.increaseSpinnerCount()),
      map(() => true),
      catchError((err: HttpErrorResponse) => {
        this.presentErrorToast(
          err,
          "Failed to update HOPE notification overrides."
        );
        return of(false);
      }),
      finalize(() => this.decreaseSpinnerCount())
    );
  }

  getHopeReportSignatures(
    assessmentId: string
  ): Observable<HopeVisitSignatureDto> {
    let url: string = `${this.dataUrl}/hqrp/visits/${assessmentId}/hope/signatures`;
    return new Observable((observer) => {
      this.increaseSpinnerCount();
      this.httpClient.get(url).subscribe(
        (data: HopeVisitSignatureDto) => {
          this.decreaseSpinnerCount();
          observer.next(data);
          observer.complete();
        },
        (err: HttpErrorResponse) => {
          this.presentErrorToast(err, "Failed to load hope report signatures.");
          this.decreaseSpinnerCount();
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  getVisitsPerformedBy(
    userId: number,
    startDate: string,
    endDate: string
  ): Observable<any> {
    return new Observable((observer) => {
      const url = `${this.dataUrl}/visits/performed-by/${userId}?startDate=${startDate}&endDate=${endDate}`;
      try {
        this.httpClient.get(url).subscribe(
          (response: any) => {
            observer.next(response.visits || []);
            observer.complete();
          },
          (err) => {
            observer.error(err);
            observer.complete();
          }
        );
      } catch (error) {
        observer.error(error);
        observer.complete();
        console.error("Error fetching overlapping visits:", error);
      }
    });
  }

  getDeathInformation(patientChartId: number): Observable<DeathInformation> {
    return new Observable((observer) => {
      const url = `${this.dataUrl}/patient-chart/${patientChartId}/death-information`;
      try {
        this.httpClient.get(url).subscribe(
          (response: any) => {
            observer.next(response);
            observer.complete();
          },
          (err) => {
            observer.error(err);
            observer.complete();
          }
        );
      } catch (error) {
        observer.error(error);
        observer.complete();
        console.error("Error fetching death information:", error);
      }
    });
  }

  updateDeathInformation(
    patientChartId: number,
    deathInformation: DeathInformation
  ): Observable<DeathInformation> {
    return new Observable((observer) => {
      const id = deathInformation.id;
      if (!id) {
        observer.error(
          new Error("Death information ID is required for update")
        );
        observer.complete();
        return;
      }
      const url = `${this.dataUrl}/patient-chart/${patientChartId}/death-information/${id}`;
      this.increaseSpinnerCount("Updating death information...");
      this.httpClient.patch(url, deathInformation).subscribe(
        (response: any) => {
          this.presentSuccessToast("Successfully updated death information.");
          this.decreaseSpinnerCount();
          observer.next(response);
          observer.complete();
        },
        (err) => {
          this.presentErrorToast(err, "Failed to update death information.");
          this.decreaseSpinnerCount();
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  getAssessmentById(assessmentId: number): Observable<Assessment> {
    return new Observable((observer) => {
      const url = `${this.dataUrl}/visits/${assessmentId}`;
      try {
        this.httpClient.get(url).subscribe(
          (response: any) => {
            observer.next(response);
            observer.complete();
          },
          (err) => {
            observer.error(err);
            observer.complete();
          }
        );
      } catch (error) {
        observer.error(error);
        observer.complete();
        console.error("Error fetching visit:", error);
      }
    });
  }

  /**
   * Send a fax for an assessment
   * @param assessmentId - The assessment ID
   * @param faxData - The fax data containing recipients, subject, etc.
   * @returns Observable that completes when fax is sent
   */
  faxAssessment(assessmentId: number, faxData: any): Observable<any> {
    const url = `${this.dataUrl}/visits/${assessmentId}/fax`;
    return new Observable((observer) => {
      this.httpClient.post(url, faxData).subscribe(
        (response) => {
          observer.next(response);
          observer.complete();
        },
        (err) => {
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  acquireAssessmentLock(assessmentId: number): Observable<any> {
    const url = `${this.dataUrl}/visits/${assessmentId}/lock`;
    return this.httpClient.post(url, {});
  }

  releaseAssessmentLock(assessmentId: number): Observable<any> {
    const url = `${this.dataUrl}/visits/${assessmentId}/lock`;
    return this.httpClient.delete(url);
  }
}



// WEBPACK FOOTER //
// ./src/providers/asmt-data-service/asmt-data-service.ts